<script setup>
import { reactive, ref, onMounted, onUnmounted } from 'vue';

// // Store management imports
import { useBreakStore } from '@/stores/break';

// // Module imports
import PollBranding from './PollBranding.vue';
import PollGoods from './PollGoods.vue';
import { PollHeroOptions } from './pollHeroUtils';

// // Component imports
import FluxButton from '@/components/FluxButton/FluxButtonComp.vue';

// // Asset imports
import pollSelectionCheckSrc from '@/assets/images/simple-polls/results-s-poll-selection-check.png';

const storeBreak = useBreakStore();

// Add a ref for updated percentages
const updatedPercentages = ref(null);
let updateTimeout = null;

const props = defineProps({
  emojiOnly: {
    type: Boolean,
    default: false,
  },
  outputBoxBackground: {
    type: Object,
    // required: true,
    default: reactive({
      background: 'darkmagenta',
    }),
  },
  adVoteState: {
    type: String,
    default: '',
  },
  adHeadline: {
    type: String,
    default: '',
  },
  adHeadlineColor: {
    type: String,
    default: 'rgba(11, 11, 11, 1)',
  },
  adTagline: {
    type: String,
    default: '',
  },
  adTaglineColor: {
    type: String,
    default: 'rgba(11, 11, 11, 1)',
  },
  preBrandCopy: {
    type: String,
    // required: true,
    default: 'Sponsored by',
  },
  pollOptions: {
    type: Array,
    // required: true,
    default: () => [
      {
        optionEntry: '1️⃣ One',
        optionLabel: PollHeroOptions.OPTION1,
        optionIndex: 0,
        optionPosition: 1,
      },
      {
        optionEntry: '2️⃣ Two',
        optionLabel: PollHeroOptions.OPTION2,
        optionIndex: 1,
        optionPosition: 2,
      },
      {
        optionEntry: '3️⃣ Three',
        optionLabel: PollHeroOptions.OPTION3,
        optionIndex: 2,
        optionPosition: 3,
      },
    ],
  },
  focusIndex: {
    type: Number,
    // required: true,
    default: 0,
  },
  brandLogos: {
    type: Array,
    default: () => [],
  },
  breakUuid: {
    type: String,
    // required: true,
    default: '',
  },
  pollPercentages: {
    type: Array,
    default: () => [],
  },
  barColor: {
    type: String,
    default: 'rgba(124, 57, 151, 1)', // Defaulting to the original purple color
  },
  resultsBoxBackgroundColor: {
    type: String,
    default: 'rgba(52, 45, 73, 1)', // Defaulting to the original dark blue-grey color
  },
});

const styleObjectHeadline = reactive({
  color: props.adHeadlineColor,
});

const styleObjectTagline = reactive({
  color: props.adTaglineColor,
});

// Function to get the percentages to display (either updated or original)
const getDisplayPercentages = () => {
  return updatedPercentages.value || props.pollPercentages;
};

// // PollInput functions

const getEmoji = (stringParam) => {
  const emojiRegex = /\p{Emoji}/gu;
  const capturedEmoji = stringParam.match(emojiRegex);

  return capturedEmoji[0];
};

const getPerc = (inputParam) => {
  const percentages = getDisplayPercentages();
  return percentages[inputParam];
};

// // PollOutput lifecycle hooks

// Set up graceful update after component is mounted
onMounted(async () => {
  // Clean up any existing timeout
  if (updateTimeout) {
    clearTimeout(updateTimeout);
    updateTimeout = null;
  }
});

// Clean up timeout on unmount
onUnmounted(() => {
  if (updateTimeout) {
    clearTimeout(updateTimeout);
    updateTimeout = null;
  }
});
</script>

<template>
  <div class="poll-output">
    <div class="poll-output__container">
      <!-- <div class="poll-output__container-wrapper-branding"> -->
      <div class="poll-output__wrapper">
        <div class="poll-output__backdrop" :style="outputBoxBackground"></div>

        <div class="poll-output__contents">
          <div class="poll-output__vote-state">
            <span class="poll-output__vote-state-copy">{{ props.adVoteState }}</span>
          </div>

          <div
            v-if="props.adTagline"
            class="poll-output__tagline out-tagline"
            :style="styleObjectTagline"
          >
            <span class="poll-output__tagline-copy">{{ props.adTagline }}</span>
          </div>
          <div
            v-else-if="props.adHeadline && !props.adTagline"
            class="poll-output__tagline out-headline"
            :style="styleObjectHeadline"
          >
            <span class="poll-output__tagline-copy">{{ props.adHeadline }}</span>
          </div>

          <!-- <div class="poll-output__separator" :style="styleObjectTagline">
              <img class="poll-output__divider" :src="overlayDividerHorizontalSrc" />
            </div> -->

          <div class="poll-output__results-row" :style="styleObjectTagline">
            <div
              v-for="item in props.pollOptions"
              :key="item.optionPosition"
              class="poll-output__outcome"
            >
              <div class="poll-output__option-button">
                <FluxButton
                  v-if="props.emojiOnly"
                  :class="{ active: item.optionIndex === props.focusIndex }"
                  :button-copy="getEmoji(item.optionEntry)"
                />
                <div
                  v-else
                  class="poll-output__results"
                  :style="{ backgroundColor: props.resultsBoxBackgroundColor }"
                >
                  <div class="poll-output__option-graph">
                    <div
                      class="poll-output__option-bargraph"
                      :style="{
                        '--widthValue': `${getDisplayPercentages()[item.optionIndex]}`,
                        'backgroundColor': props.barColor,
                      }"
                    ></div>
                  </div>
                  <div
                    class="poll-output__final-options"
                    :class="{ active: item.optionIndex === props.focusIndex }"
                  >
                    <div class="poll-output__final-options--copy">
                      <img
                        v-if="item.optionIndex === props.focusIndex"
                        class="poll-output__final-options--check"
                        :src="pollSelectionCheckSrc"
                      />
                      {{ item.optionEntry }}
                    </div>

                    <div class="poll-output__final-options--percentage">
                      {{ getPerc(item.optionIndex) }}%
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="props.emojiOnly" class="poll-output__option-graph">
                <div
                  class="poll-output__option-bargraph"
                  :style="{
                    width: `${getPerc(item.optionIndex)}%`,
                    backgroundColor: props.barColor,
                  }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <PollBranding
          v-if="props.brandLogos"
          :poll-pre-brand-copy="props.preBrandCopy"
          :poll-pre-brand-color="styleObjectTagline"
          :poll-brand-logos="props.brandLogos"
        />
      </div>
      <!-- </div> -->

      <PollGoods
        v-if="props.emojiOnly"
        :goods-data="storeBreak.breakProducts"
        :interactive-break-uuid="storeBreak.breakUuid"
        :ad-id="storeBreak.breakGeneratedIdentifier"
      />
    </div>
  </div>
</template>
